import React, { Suspense, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { AddButton, PageSpinner } from '@td-client-frontend/common';
import { ExportIcon, ViewFile } from '@td-client-frontend/icons';
import { GroupContextProvider, useGroupContext } from '../../context/GroupContext';
import { useGroupPage } from '../../hooks/useGroupsPage';
import { GroupsForm } from './GroupsForm';
import WithPermission, { PermMode } from '../../components/WithPermission';
import { useFormData } from '../../hooks/useFormData';
import { TransformedGroupsPackage } from '../../@types/Groups';
import { NewGroupModal } from './components/NewGroupModal';
import { SideMenuContent } from './components/SideMenuContent';
import { GroupType } from '../../api/api';

const EmployerGroups = () => {
  const history = useHistory();
  const [isShowGroupModal, setShowGroupModal] = useState<boolean>(false);
  const { type, groupId, groupName, setBenefitIdx, onTypeChange, setInitialValues } = useGroupContext();
  const {
    groupData: { data: defaultValues, isFetching },
    exportDrafts
  } = useGroupPage({ groupId, type, setInitialValues });
  const { insertFormData, submitData, updateFormData, removeFormData } = useFormData({ id: groupId, type });

  const handleShowGroupModal = useCallback(() => setShowGroupModal((value: boolean) => !value), []);

  const handleCreateDraft = useCallback(
    (data: { name: string; planNumber: string }) => {
      handleShowGroupModal();
      insertFormData({ data: { ...data, 'contacts--Reporters': [null] }, type: GroupType.DRAFT });
      onTypeChange(GroupType.DRAFT);
      setBenefitIdx(null);
    },
    [handleShowGroupModal, insertFormData, onTypeChange, setBenefitIdx]
  );

  const handleDeleteDraft = useCallback(
    () => removeFormData({ type: GroupType.DRAFT, id: groupId }),
    [groupId, removeFormData]
  );
  const handlePublish = useCallback((data: TransformedGroupsPackage) => submitData({ data, type }), [submitData, type]);

  const handleViewActivityLogClick = useCallback(() => {
    history.push(`/activity-log?employerName=${groupName}`);
  }, [history, groupName]);

  return (
    <div className="flex flex-1 flex-col gap-4 px-6 w-full h-full overflow-y-auto">
      <div className="flex justify-between">
        <h1 className="text-4xl font-effra mt-8 mb-4">Employer Groups</h1>
        <div className="flex gap-4">
          {!!groupName && (
            <WithPermission requiredPerms={['AuditRead']}>
              <AddButton onClick={handleViewActivityLogClick} label="View activity log" Icon={<ViewFile />} />
            </WithPermission>
          )}
          <WithPermission requiredPerms={['GroupCreate', 'DraftCreate']} mode={PermMode.ANY}>
            <AddButton onClick={handleShowGroupModal} label="New group" />
          </WithPermission>
          {type === GroupType.DRAFT && (
            <WithPermission requiredPerms={['DraftRead']} mode={PermMode.ANY}>
              <button onClick={exportDrafts} type="button" className="flex items-center space-x-2 btn btn-default">
                <ExportIcon aria-hidden />
                <p className="text-primary-500">Export all available drafts</p>
              </button>
            </WithPermission>
          )}
        </div>
      </div>
      <div className="flex flex-1 gap-8 h-full w-full">
        <SideMenuContent handleShowGroupModal={handleShowGroupModal} />
        <>
          {isFetching ? (
            <div className=" w-full relative">
              <div className="absolute inset-0 flex items-center justify-center w-full bg-white opacity-75 z-20">
                <PageSpinner />
              </div>
            </div>
          ) : (
            <GroupsForm
              defaultValues={defaultValues!}
              type={type!}
              onDraftDelete={handleDeleteDraft}
              onSaveAsDraft={updateFormData}
              onPublish={handlePublish}
            />
          )}
        </>
      </div>
      {isShowGroupModal && <NewGroupModal onCancel={handleShowGroupModal} onSubmit={handleCreateDraft} />}
    </div>
  );
};

const SuspenseWithLoader = (props: any) => {
  return (
    <Suspense fallback={<PageSpinner />}>
      <GroupContextProvider>
        <EmployerGroups {...props} />
      </GroupContextProvider>
    </Suspense>
  );
};

export default SuspenseWithLoader;
